import React, { memo, useEffect, useRef, useState } from 'react';
import {Helmet} from "react-helmet";
import parse from 'html-react-parser';
import config from 'config';
import { getStoreContentScripts } from '../../apis/webStoreAPI';
import useLocalize from '../../hooks/useLocalize';

// Ref: https://stackoverflow.com/a/66282162/6680602
const InjectScript = memo(({ script }) => {
	const divRef = useRef(null);

	useEffect(() => {
		if (divRef.current === null) {
			return;
		}
		// create a contextual fragment that will execute the script
		// beware of security concerns!!

		const doc = document.createRange().createContextualFragment(script);

		// clear the div HTML, and append the doc fragment with the script
		divRef.current.innerHTML = '';
		divRef.current.appendChild(doc);

		// eslint-disable-next-line consistent-return
		return () => {};
	}, []);

	return <div ref={divRef} />;
});

const ContentScripts = ({ storeId }) => {
	const { enabled, activeLocale, defaultLocale } = useLocalize();

	const [scripts, setScripts] = useState([]);
	const currentEnv = config.ENV;

	const updateScripts = () => {
		getStoreContentScripts(storeId).then((response) => {
			setScripts(response);
		});
	};

	useEffect(() => {
		updateScripts();
	}, []);

	useEffect(() => {
		updateScripts();
	}, [activeLocale, defaultLocale, enabled]);

	const getContent = (script) => {
		if ('translations' in script) {
			let response;
			if (defaultLocale in script.translations) {
				response = script.translations[defaultLocale].content;
			}

			if (
				activeLocale in script.translations &&
				script.translations[activeLocale].content
			) {
				response = script.translations[activeLocale].content;
			}

			return response;
		}
		return script.props.content;
	};

	const scriptEnvShow = (script) => {
		let key;
		if (['dev', 'beta'].includes(currentEnv)) {
			key = 'beta';
		} else if (['client', 'prod'].includes(currentEnv)) {
			key = 'prod';
		}

		return script.environment_visibility.rules.includes(key);
	};

	return scripts && scripts.length > 0
		? scripts.map(
				(script) =>
					scriptEnvShow(script) && (
						script?.display_region === "head" ? (
							<Helmet>
								{parse(getContent(script))}
						</Helmet>
						): (
							<InjectScript
							key={script.id}
							script={getContent(script)}
						/>
						)
					),
		  )
		: null;
};

export default ContentScripts;
